// ============================================================
// CodesTab.jsx - Access & Codes management for one property.
// Shows guest codes (tied to reservations) and standalone codes
// (cleaner/maintenance/owner), with a form to generate new
// standalone codes either auto or by typing a real number.
// ============================================================

import { useState, useEffect, useCallback } from 'react';

const IconPlus = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 5v14M5 12h14"/></svg>;
const IconTrash = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>;
const IconKey = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="8" cy="15" r="3"/><path d="M10.5 12.5L18 5l2 2-1.5 1.5M15 8l2 2"/></svg>;

const PURPOSE_LABELS = { guest: 'Invite', cleaner: 'Menage', maintenance: 'Maintenance', owner: 'Proprietaire', other: 'Autre' };
const PURPOSE_COLORS = { guest: 'bb', cleaner: 'bg', maintenance: 'ba', owner: 'bgr', other: 'bgr' };

function NewCodeForm({ propertyId, m, toast, onCreated }) {
  const [open, setOpen] = useState(false);
  const [purpose, setPurpose] = useState('cleaner');
  const [label, setLabel] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [entryMethod, setEntryMethod] = useState('auto');
  const [manualCode, setManualCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!validFrom || !validUntil) { toast('Dates requises', 'err'); return; }
    if (entryMethod === 'manual' && !manualCode.trim()) { toast('Code requis pour la saisie manuelle', 'err'); return; }

    setSubmitting(true);
    try {
      const res = await m(`/admin/properties/${propertyId}/codes`, {
        method: 'POST',
        body: JSON.stringify({
          purpose, label: label || null,
          valid_from: new Date(validFrom).toISOString(),
          valid_until: new Date(validUntil).toISOString(),
          entry_method: entryMethod,
          manual_code: entryMethod === 'manual' ? manualCode.trim() : undefined,
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast('Code cree', 'ok');
        setLabel(''); setValidFrom(''); setValidUntil(''); setManualCode(''); setOpen(false);
        onCreated();
      } else {
        toast(data.error || 'Erreur', 'err');
      }
    } catch (e) {
      toast('Erreur reseau', 'err');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button className="add-step-btn" onClick={() => setOpen(true)} type="button" style={{ marginBottom: 16 }}>
        {IconPlus} Ajouter un code (menage, maintenance, proprietaire...)
      </button>
    );
  }

  return (
    <div className="card" style={{ padding: 16, marginBottom: 16 }}>
      <div className="frow">
        <div className="ff">
          <label className="fl">Type</label>
          <select className="fsel2" value={purpose} onChange={(e) => setPurpose(e.target.value)}>
            <option value="cleaner">Menage</option>
            <option value="maintenance">Maintenance</option>
            <option value="owner">Proprietaire</option>
            <option value="other">Autre</option>
          </select>
        </div>
        <div className="ff">
          <label className="fl">Label (qui / pourquoi)</label>
          <input className="fi" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Fatima - menage du jeudi" />
        </div>
      </div>

      <div className="frow">
        <div className="ff">
          <label className="fl">Valide a partir de</label>
          <input className="fi" type="datetime-local" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
        </div>
        <div className="ff">
          <label className="fl">Valide jusqu'a</label>
          <input className="fi" type="datetime-local" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
        </div>
      </div>

      <div className="ff">
        <label className="fl">Methode</label>
        <div className="tabs" style={{ marginBottom: 0 }}>
          <button type="button" className={`tab ${entryMethod === 'auto' ? 'on' : ''}`} onClick={() => setEntryMethod('auto')}>
            Generer automatiquement
          </button>
          <button type="button" className={`tab ${entryMethod === 'manual' ? 'on' : ''}`} onClick={() => setEntryMethod('manual')}>
            Saisir un code reel
          </button>
        </div>
      </div>

      {entryMethod === 'manual' && (
        <div className="ff">
          <label className="fl">Code (tel que configure sur la serrure)</label>
          <input className="fi" value={manualCode} onChange={(e) => setManualCode(e.target.value)} placeholder="123456" style={{ fontFamily: 'var(--font-mono)', maxWidth: 160 }} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-p" onClick={submit} disabled={submitting} type="button">
          {submitting ? <span className="spin" /> : 'Creer le code'}
        </button>
        <button className="btn btn-s" onClick={() => setOpen(false)} type="button">Annuler</button>
      </div>
    </div>
  );
}

export default function CodesTab({ propertyId, m, toast }) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExpired, setShowExpired] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    m(`/admin/properties/${propertyId}/codes${showExpired ? '?includeExpired=1' : ''}`).then(r => r.json())
      .then(d => { if (d.success) setCodes(d.data); })
      .finally(() => setLoading(false));
  }, [propertyId, m, showExpired]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (codeId) => {
    if (!window.confirm('Revoquer ce code ?')) return;
    const res = await m(`/admin/codes/${codeId}`, { method: 'DELETE' });
    const data = await res.json();
    if (data.success) { toast('Code revoque', 'ok'); load(); }
    else toast(data.error || 'Erreur', 'err');
  };

  return (
    <div>
      <NewCodeForm propertyId={propertyId} m={m} toast={toast} onCreated={load} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>Codes actifs</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--g2)', cursor: 'pointer' }}>
          <input type="checkbox" checked={showExpired} onChange={(e) => setShowExpired(e.target.checked)} />
          Inclure les codes expires
        </label>
      </div>

      {loading ? <div className="empty"><span className="spin" /></div> : codes.length === 0 ? (
        <div className="empty">
          <div className="empty-ico">{IconKey}</div>
          <div className="empty-t">Aucun code</div>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Code</th>
                <th>Label / Invite</th>
                <th>Validite</th>
                <th>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {codes.map(code => (
                <tr key={code.id}>
                  <td><span className={`badge ${PURPOSE_COLORS[code.purpose] || 'bgr'}`}>{PURPOSE_LABELS[code.purpose] || code.purpose}</span></td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{code.code || '—'}</td>
                  <td style={{ fontSize: 12 }}>{code.label || code.guest_full_name || '—'}</td>
                  <td style={{ fontSize: 11, color: 'var(--g2)' }}>
                    {new Date(code.valid_from).toLocaleDateString('fr-FR')} → {new Date(code.valid_until).toLocaleDateString('fr-FR')}
                  </td>
                  <td>
                    <span className={`badge ${code.status === 'verified' ? 'bg' : code.status === 'expired' ? 'bgr' : 'ba'}`}>
                      {code.status}
                    </span>
                  </td>
                  <td>
                    {code.status === 'verified' && (
                      <button className="btn btn-d btn-sm btn-icon" onClick={() => revoke(code.id)} type="button" title="Revoquer">
                        {IconTrash}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
